import { Component, ComponentPropDefinitions, ComponentProps, Label, PanelProps } from "../..";
import { CompoundComponent } from "../compound/CompoundComponent";
import { ComponentUtil } from "../../base/ComponentUtil";
import { PropsAccessLabelCreator } from "../../PropsAccessLabelCreator";

const PROPS_CTA = "__propsCTA";

export interface ComponentConfig {
    value: Component;
    propName: string;
    getPropertyDefinitionsOverride?: () => ComponentPropDefinitions;
    defaultProps?: Partial<ComponentProps>;
    overrideProps?: Partial<ComponentProps>;
}

/**
 * A compound component that allows inner component properties to be set in the UI Designer.
 */
export abstract class EnhancedCompoundComponent extends CompoundComponent {
    private _innerComponentsIdentified = false;

    constructor(props?: Partial<PanelProps>, callSetProps: boolean = true) {
        super(props, callSetProps);
        this.allowDropInDesigner = false;
    }

    protected get innerComponentsIdentified(): boolean {
        return this._innerComponentsIdentified;
    }

    private set innerComponentsIdentified(value: boolean) {
        this._innerComponentsIdentified = value;
    }

    protected override _initialDropInDesigner() {
        // We aren't deserializing, so go ahead and handle things like adding the props access labels as we add the
        // inner components in the designer.
        this.innerComponentsIdentified = true;
    }

    protected override doAfterChildAdded(child: Component) {
        super.doAfterChildAdded(child);
        if (child == null) {
            return;
        }
        this.syncInnerComponentIds();
        child.excludeFromAutogenLayout = true;
        child.draggableInDesigner = false;
        child.deletableInDesigner = false;
        child.alternateDesignerTarget = this;
        child._designer = this._designer;
        this.syncPropsAccessLabel(child);
    }

    protected syncInnerComponents() {
        this.identifyInnerComponents();
        this.innerComponentsIdentified = true;
        this.syncAllPropsAccessLabels();
    }

    protected abstract identifyInnerComponents(): void;

    override doAfterDeserialize() {
        this.syncInnerComponents();
    }

    override doAfterClone() {
        this.syncInnerComponents();
    }

    override _applyEnabled(value: boolean): void {
        this.components?.forEach(c => c.enabled = value);
        if (value) {
            this._element.removeAttribute("disabled");
        }
        else {
            this._element.setAttribute("disabled", "true");
        }
    }

    /**
     * Sets or updates an internal components.
     *
     * @param config - The configuration object containing the following properties:
     *   - `value`: The new component to set or update.
     *   - `propName`: The name of the property where the component is stored.
     *   - `getPropertyDefinitionsOverride`: A function to override property definitions for the component.
     *   - `defaultProps`: Default properties to apply if not explicitly set on the component.
     *   - `overrideProps`: Properties that will always be set on the component.
     */
    protected setInternalComponent(config: ComponentConfig, nullComponent: boolean = false): void {
        const { value, propName, getPropertyDefinitionsOverride, defaultProps, overrideProps } = config;
        const currentComp = this[propName];
        if (value == null) {
            this.remove(currentComp);
            if (nullComponent === true)
                this[propName] = value;    
            return;
        }
        if (currentComp === value) return;
        this[propName] = value;
        const newComp = this[propName];
        if (getPropertyDefinitionsOverride != null)
            newComp.getPropertyDefinitions = getPropertyDefinitionsOverride;
        if (defaultProps != null)
            this.setInnerComponentProps(newComp, defaultProps, true);
        if (overrideProps != null)
            this.setInnerComponentProps(newComp, overrideProps);
        this.remove(currentComp);
        this.addInternalComponent(newComp);
        this.doAfterSetInternalComponent(newComp);
    }

    protected abstract addInternalComponent(component: Component): void;

    protected doAfterSetInternalComponent(_component: Component) {
    }

    protected setInnerComponentProps(component: Component, props: Partial<ComponentProps>, setOnlyWhenNull: boolean = false) {
        if (component != null) {
            if (setOnlyWhenNull) {
                for (const key in props) {
                    if (component[`_${key}`] == null) {
                        component[key] = props[key];
                    }
                }
            } else {
                component.setProps(props);
            }
            component.addPropertyDefaultValueOverrides(props);
        }
    }

    protected setInnerComponentId(component: Component,  value: string) {
        this.setInnerComponentProps(component, {id: ComponentUtil.createPrefixedId(this.id, value)});
    }

    protected getPropsCTA(component: Component): Label {
        return component[PROPS_CTA];
    }

    private syncAllPropsAccessLabels() {
        this.components.forEach(component => this.syncPropsAccessLabel(component));
    }

    protected syncPropsAccessLabel(component: Component) {
        if (this._designer?.allowsDrop === true && this.innerComponentsIdentified === true && component != null &&
            this.getPropsCTA(component) == null) {
                const propsAccessLabel = this.createAndAddPropsAccessLabel(component);
                component[PROPS_CTA] = propsAccessLabel;
        }
    }

    protected removePropsAccessLabel(component: Component) {
        if (component == null)
            return;

        this.getPropsCTA(component)?._element?.remove();
        delete component[PROPS_CTA];
    }

    protected createAndAddPropsAccessLabel(parentComponent: Component): Label {
        const label = PropsAccessLabelCreator.create({
            id: parentComponent.createInnerComponentId("propsAccessLabel"),
            text: "Props",
            fontSize: "small",
            fontBold: true,
            borderWidth: 0,
            backgroundColor: "transparent",
            _designer: this._designer,
            tooltip: () => {
                const caption = parentComponent["caption"];
                const compName = parentComponent.properName;
                return `Click to access the properties for the ${caption ? `${caption} ${compName}` : compName}.`;
            }
        });
        label.alternateDesignerTarget = parentComponent;
        parentComponent.displayPropsAccessLabel(label);
        return label;
    }
}
